import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  History, ChevronDown, ChevronRight, ArrowUpCircle, ArrowDownCircle,
  Minus, Loader2, Clock, User, Hash, AlertTriangle, CheckCircle,
  Star, FileText, Zap,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/** Get human-readable DTC description from the code pattern */
const getDTCDescription = (code) => {
  if (!code) return '';
  const KNOWN = {
    'P0017': 'Crankshaft/Camshaft Position Correlation (Bank 1 Sensor B)',
    'P0018': 'Crankshaft/Camshaft Position Correlation (Bank 2 Sensor A)',
    'P001C': 'Camshaft Position - Intake Timing Over-Advanced (Bank 1)',
    'P001D': 'Camshaft Position - Intake Timing Over-Retarded (Bank 1)',
    'P002B': 'Intake Valve Control Performance (Bank 1)',
    'P0050': 'HO2S Heater Control Circuit (Bank 2 Sensor 1)',
    'P0100': 'Mass Air Flow Sensor Circuit',
    'P0120': 'Throttle Position Sensor Circuit',
    'P0171': 'System Too Lean (Bank 1)',
    'P0200': 'Injector Circuit / Open',
    'P0300': 'Random/Multiple Cylinder Misfire Detected',
    'P0420': 'Catalyst System Efficiency Below Threshold',
    'P0500': 'Vehicle Speed Sensor',
    'P0503': 'Vehicle Speed Sensor Intermittent/Erratic',
    'P0532': 'A/C Refrigerant Pressure Sensor Circuit Low',
    'P0605': 'Internal Control Module ROM Error',
    'P0701': 'Transmission Control System Range/Performance',
    'P1100': 'MAF Sensor Intermittent',
    'P1527': 'Transmission Range Sensor Circuit',
    'P2000': 'NOx Trap Efficiency Below Threshold (Bank 1)',
    'P2140': 'Throttle Position Sensor Voltage Correlation',
    'P2240': 'Oxygen Sensor Positive Current Circuit (Bank 2 Sensor 1)',
    'P2720': 'Solenoid F Performance/Stuck Off',
    'P3400': 'Cylinder Deactivation System',
    'P3650': 'Charging System Voltage',
    'C0002': 'ABS Module Performance',
    'C0004': 'ABS Wheel Speed Sensor',
    'C0007': 'ABS Control Module Voltage',
    'C0018': 'ABS Pressure Sensor',
    'C0090': 'Brake Switch Circuit',
    'C1002': 'Steering Angle Sensor',
    'C1006': 'Yaw Rate Sensor',
    'C1018': 'ABS Hydraulic Pump Motor',
    'C1090': 'Brake Booster Performance',
    'C2A00': 'Electronic Parking Brake',
    'B0450': 'Driver Airbag Module',
    'B0550': 'ECU Backup Power',
    'U0100': 'Lost Communication with ECM/PCM',
    'U2E00': 'Control Module Configuration',
    'U2E50': 'Control Module Communication Error',
    'U2F00': 'CAN Bus Off',
    'U2F50': 'Module Internal Communication',
    'U3000': 'Control Module Error',
    'U3100': 'Control Module Not Programmed',
  };
  if (KNOWN[code]) return KNOWN[code];
  // Fallback: generate from code prefix
  const prefix = code[0];
  const prefixNames = { P: 'Powertrain', C: 'Chassis', B: 'Body', U: 'Network' };
  return `${prefixNames[prefix] || 'Unknown'} system code`;
};

/** Get clear, convincing explanation for why this code was filtered */
const getFilterReason = (statusNum, statusHex) => {
  const s = typeof statusNum === 'number' ? statusNum : parseInt(statusHex || '0', 16);
  if (isNaN(s) || s === 0)
    return 'No active fault — the vehicle\'s computer monitors this system and currently reports no malfunction';

  const hasTestFailed = (s & 0x01) !== 0;
  const hasPending = (s & 0x04) !== 0;
  const hasConfirmed = (s & 0x08) !== 0;
  const hasTestIncomplete = (s & 0x10) !== 0;
  const hasFailedSinceClear = (s & 0x20) !== 0;
  const hasTestNotRun = (s & 0x40) !== 0;
  const hasWarningLamp = (s & 0x80) !== 0;

  if (hasTestNotRun && !hasConfirmed && !hasTestFailed)
    return 'The vehicle\'s computer has this system in its watchlist but has not flagged any fault — this is a routine monitoring entry, not an active problem';

  if (hasPending && !hasConfirmed)
    return 'One-time detection that the vehicle\'s computer has not verified — cars require a fault to repeat across multiple trips before classifying it as a confirmed problem';

  if (hasTestIncomplete && !hasConfirmed)
    return 'The vehicle\'s internal self-test for this system is still in progress — this is normal and does not indicate a fault. The car\'s computer will confirm it only if a problem is found';

  if (hasFailedSinceClear && !hasConfirmed && !hasTestFailed)
    return 'Was flagged at some point in the past but the vehicle\'s computer no longer considers it an active fault — the issue may have resolved itself';

  if (hasConfirmed && hasTestNotRun)
    return 'Historical entry in the vehicle\'s memory — the car\'s computer retains this from a previous period but does not classify it as a current confirmed fault';

  if (hasConfirmed && !hasTestFailed)
    return 'Stored in vehicle history but not currently active — the car\'s computer had flagged this previously but the fault is no longer being reproduced';

  if (hasTestFailed && !hasConfirmed)
    return 'Single occurrence that the vehicle\'s computer has not promoted to a confirmed fault — intermittent readings that don\'t persist are not classified as real problems';

  if (hasTestFailed && hasConfirmed)
    return 'Present in vehicle memory but excluded by the car\'s computer from its confirmed fault list — indicates a stale or resolved entry';

  if (hasWarningLamp && !hasConfirmed)
    return 'Warning indicator was historically triggered but the underlying fault is no longer confirmed by the vehicle\'s computer';

  return 'The vehicle\'s computer did not classify this as a confirmed fault — does not meet the threshold for a verified malfunction';
};

const ScanHistory = ({ inspectionId, onActiveScanChanged, apiBase = '/api/inspections' }) => {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [expandedScan, setExpandedScan] = useState(null);
  const [activeScanNumber, setActiveScanNumber] = useState(null);
  const [pinning, setPinning] = useState(null);
  const [manualCodes, setManualCodes] = useState([]);
  const [togglingCode, setTogglingCode] = useState(null);

  const fetchHistory = useCallback(async () => {
    if (!inspectionId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${apiBase}/${inspectionId}/obd-scan-history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setScans(res.data.scans || []);
      setActiveScanNumber(res.data.active_scan_number ?? null);
      setManualCodes(res.data.manual_codes || []);
    } catch (err) {
      console.error('Failed to load scan history:', err);
    } finally {
      setLoading(false);
    }
  }, [inspectionId]);

  useEffect(() => {
    if (expanded && scans.length === 0) fetchHistory();
  }, [expanded, fetchHistory]);

  const handlePinScan = async (scanNumber) => {
    if (pinning) return;
    setPinning(scanNumber);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}${apiBase}/${inspectionId}/obd-active-scan`,
        { scan_number: scanNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveScanNumber(scanNumber);
      toast.success(`Scan #${scanNumber} is now the report source. Re-run AI Analysis to update.`);
      if (onActiveScanChanged) onActiveScanChanged(scanNumber);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set active scan');
    } finally {
      setPinning(null);
    }
  };

  const handleToggleManualCode = async (code) => {
    setTogglingCode(code);
    try {
      const token = localStorage.getItem('token');
      const isIncluded = manualCodes.includes(code);
      const newCodes = isIncluded ? manualCodes.filter(c => c !== code) : [...manualCodes, code];
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}${apiBase}/${inspectionId}/obd-manual-codes`,
        { codes: newCodes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setManualCodes(newCodes);
      toast.success(isIncluded ? `${code} removed from report` : `${code} added to report`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update manual codes');
    } finally {
      setTogglingCode(null);
    }
  };

  if (!inspectionId) return null;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="obd-scan-history">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid="scan-history-toggle"
      >
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-slate-600" />
          <span className="font-semibold text-sm text-slate-700">Scan History</span>
          {scans.length > 0 && (
            <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">
              {scans.length} scan{scans.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="p-3 space-y-2 bg-white">
          {loading ? (
            <div className="flex items-center justify-center py-6 gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              <span className="text-sm text-slate-500">Loading scan history...</span>
            </div>
          ) : scans.length === 0 ? (
            <div className="text-center py-6">
              <History className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No scans recorded yet</p>
              <p className="text-xs text-slate-400 mt-1">Scan history will appear here after the mechanic performs an OBD scan</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Active scan info banner */}
              {scans.length > 1 && activeScanNumber && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="active-scan-banner">
                  <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />
                  <span>Scan <strong>#{activeScanNumber}</strong> is the report source. AI analysis and PDF will use this scan's data.</span>
                </div>
              )}

              {scans.map((scan, idx) => {
                const isExpanded = expandedScan === scan.id;
                const isLatest = idx === scans.length - 1;
                const isActive = scan.scan_number === activeScanNumber;
                const diff = scan.diff;
                
                return (
                  <div
                    key={scan.id}
                    className={`border rounded-lg overflow-hidden transition-all ${
                      isActive
                        ? 'border-amber-300 bg-amber-50/30 ring-1 ring-amber-200'
                        : isLatest
                          ? 'border-emerald-300 bg-emerald-50/30'
                          : 'border-slate-200'
                    }`}
                    data-testid={`scan-history-item-${scan.scan_number}`}
                  >
                    <button
                      onClick={() => setExpandedScan(isExpanded ? null : scan.id)}
                      className="w-full flex items-center justify-between p-2.5 hover:bg-slate-50/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${
                          isActive ? 'bg-amber-500 text-white' : isLatest ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                        }`}>
                          {scan.scan_number}
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-800">
                              Scan #{scan.scan_number}
                            </span>
                            {isActive && (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5" data-testid={`scan-active-badge-${scan.scan_number}`}>
                                <Star className="h-2.5 w-2.5 fill-amber-500" />Report Source
                              </span>
                            )}
                            {isLatest && !isActive && (
                              <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium">
                                Latest
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(scan.scanned_at).toLocaleString('en-IN', { 
                                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                              })}
                            </span>
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {scan.scanned_by_name}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* DTC count badge */}
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          scan.dtc_count === 0 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : scan.dtc_count <= 5 
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                        }`}>
                          {scan.dtc_count} DTC{scan.dtc_count !== 1 ? 's' : ''}
                        </span>
                        
                        {/* Diff indicator for rescans */}
                        {diff && (
                          <div className="flex items-center gap-1">
                            {diff.codes_added.length > 0 && (
                              <span className="text-[10px] text-red-600 font-medium flex items-center gap-0.5">
                                <ArrowUpCircle className="h-3 w-3" />+{diff.codes_added.length}
                              </span>
                            )}
                            {diff.codes_removed.length > 0 && (
                              <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-0.5">
                                <ArrowDownCircle className="h-3 w-3" />-{diff.codes_removed.length}
                              </span>
                            )}
                            {diff.codes_added.length === 0 && diff.codes_removed.length === 0 && (
                              <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                                <Minus className="h-3 w-3" />same
                              </span>
                            )}
                          </div>
                        )}
                        
                        {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-slate-100 p-2.5 space-y-2 bg-white">
                        {/* Use for Report button */}
                        {scans.length > 1 && !isActive && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handlePinScan(scan.scan_number); }}
                            disabled={pinning === scan.scan_number}
                            className="w-full border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400"
                            data-testid={`pin-scan-${scan.scan_number}`}
                          >
                            {pinning === scan.scan_number ? (
                              <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />Setting...</>
                            ) : (
                              <><Star className="h-3.5 w-3.5 mr-1.5" />Use for Report</>
                            )}
                          </Button>
                        )}
                        {isActive && scans.length > 1 && (
                          <div className="flex items-center gap-2 py-1.5 px-2.5 bg-amber-50 rounded-md border border-amber-200 text-xs text-amber-700" data-testid={`active-indicator-${scan.scan_number}`}>
                            <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500 flex-shrink-0" />
                            <span>This scan's data is used for AI Analysis and the PDF Report</span>
                          </div>
                        )}

                        {/* Diff section for rescans */}
                        {diff && (diff.codes_added.length > 0 || diff.codes_removed.length > 0) && (
                          <div className="flex gap-2 mb-2">
                            {diff.codes_added.length > 0 && (
                              <div className="flex-1 bg-red-50 border border-red-100 rounded-md p-2">
                                <p className="text-[10px] font-semibold text-red-600 mb-1">
                                  New codes (+{diff.codes_added.length})
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {diff.codes_added.map(c => (
                                    <span key={c} className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-mono">
                                      {c}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {diff.codes_removed.length > 0 && (
                              <div className="flex-1 bg-emerald-50 border border-emerald-100 rounded-md p-2">
                                <p className="text-[10px] font-semibold text-emerald-600 mb-1">
                                  Resolved (-{diff.codes_removed.length})
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {diff.codes_removed.map(c => (
                                    <span key={c} className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-mono line-through">
                                      {c}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Meta row */}
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                          {scan.protocol && (
                            <span>Protocol: <span className="font-medium text-slate-700">{scan.protocol}</span></span>
                          )}
                          {scan.vin && (
                            <span>VIN: <span className="font-medium text-slate-700 font-mono">{scan.vin}</span></span>
                          )}
                          {scan.manufacturer && (
                            <span>Make: <span className="font-medium text-slate-700">{scan.manufacturer}</span></span>
                          )}
                          {scan.live_data_count > 0 && (
                            <span>Live PIDs: <span className="font-medium text-slate-700">{scan.live_data_count}</span></span>
                          )}
                          {scan.scan_duration_seconds > 0 && (
                            <span>Duration: <span className="font-medium text-slate-700">{Math.round(scan.scan_duration_seconds)}s</span></span>
                          )}
                        </div>

                        {/* Protocol Retry Log */}
                        {scan.protocol_retry_log && (
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-2" data-testid={`protocol-retry-${scan.scan_number}`}>
                            <p className="text-[10px] font-semibold text-blue-600 mb-0.5">Protocol Retry Log</p>
                            <p className="text-[10px] text-blue-700 font-mono leading-relaxed whitespace-pre-wrap">{scan.protocol_retry_log}</p>
                          </div>
                        )}
                        
                        {/* Shadow Diagnostic / Verification */}
                        {(() => {
                          const hasCodes = scan.shadow_diagnostic_codes?.length > 0;
                          const shadowText = scan.shadow_diagnostic || '';
                          const isCleanShadow = !hasCodes && (
                            shadowText.includes('NO DATA') ||
                            shadowText.includes('Filtered out: 0') ||
                            shadowText.includes('found 0 codes')
                          );
                          const hasAnyData = hasCodes || shadowText;

                          if (isCleanShadow) {
                            // Both scans agree — show positive verification
                            return (
                              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 flex items-start gap-2" data-testid={`shadow-clean-${scan.scan_number}`}>
                                <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-[11px] font-semibold text-emerald-700">Verified Clean</p>
                                  <p className="text-[10px] text-emerald-600 mt-0.5">Both standard and deep OBD scans confirmed no fault codes on this vehicle</p>
                                </div>
                              </div>
                            );
                          }

                          if (!hasAnyData) return null;

                          // Has filtered codes — show the detailed breakdown
                          return (
                            <div className="bg-violet-50 border border-violet-200 rounded-lg overflow-hidden" data-testid={`shadow-diag-${scan.scan_number}`}>
                              <div className="px-3 py-2 border-b border-violet-200">
                                <p className="text-[10px] font-semibold text-violet-600">Additional Codes — Not confirmed by vehicle</p>
                                <p className="text-[9px] text-violet-400 mt-0.5">The deep scan detected these in the vehicle's monitoring system, but the car's own computer does not classify them as confirmed faults.</p>
                              </div>
                              {hasCodes && (
                                <div className="divide-y divide-violet-100">
                                  {scan.shadow_diagnostic_codes.map((sc) => {
                                    const isIncluded = manualCodes.includes(sc.code);
                                    const isToggling = togglingCode === sc.code;
                                    const desc = sc.description || getDTCDescription(sc.code);
                                    const filterReason = getFilterReason(sc.status, sc.statusHex);
                                    return (
                                      <div key={sc.code} className={`px-3 py-2 ${isIncluded ? 'bg-emerald-50' : ''}`}>
                                        <div className="flex items-center justify-between">
                                          <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                              <span className="font-mono text-xs font-bold text-violet-800">{sc.code}</span>
                                              <span className="text-[10px] text-slate-600 truncate">{desc}</span>
                                            </div>
                                            <p className="text-[10px] text-orange-600 mt-0.5 flex items-start gap-1">
                                              <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                              <span>{filterReason}</span>
                                            </p>
                                          </div>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); handleToggleManualCode(sc.code); }}
                                            disabled={isToggling}
                                            data-testid={`toggle-shadow-${sc.code}`}
                                            className={`flex-shrink-0 ml-3 px-2.5 py-1 rounded text-[10px] font-semibold transition-all ${
                                              isIncluded
                                                ? 'bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300'
                                                : 'bg-violet-100 text-violet-600 border border-violet-300 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300'
                                            }`}
                                          >
                                            {isToggling ? '...' : isIncluded ? 'Included' : 'Include'}
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {manualCodes.length > 0 && (
                                <div className="px-3 py-1.5 bg-emerald-50 border-t border-violet-200 flex items-center gap-1.5" data-testid="manual-codes-summary">
                                  <CheckCircle className="h-3 w-3 text-emerald-500" />
                                  <span className="text-[10px] text-emerald-700 font-medium">{manualCodes.length} code{manualCodes.length !== 1 ? 's' : ''} manually included: {manualCodes.join(', ')}</span>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* DTC codes table */}
                        {scan.dtc_codes.length > 0 ? (
                          <div className="overflow-auto max-h-48 border border-slate-100 rounded-md">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-50 sticky top-0">
                                <tr>
                                  <th className="text-left px-2 py-1.5 font-medium text-slate-500">Code</th>
                                  <th className="text-left px-2 py-1.5 font-medium text-slate-500">Description</th>
                                  <th className="text-left px-2 py-1.5 font-medium text-slate-500">Status</th>
                                  <th className="text-left px-2 py-1.5 font-medium text-slate-500">Source</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-50">
                                {scan.dtc_codes.map((dtc, i) => {
                                  const isNew = diff?.codes_added?.includes(dtc.code);
                                  return (
                                    <tr key={i} className={isNew ? 'bg-red-50/50' : ''}>
                                      <td className="px-2 py-1.5 font-mono font-medium text-slate-800">
                                        {dtc.code}
                                        {isNew && <span className="ml-1 text-[9px] text-red-500 font-sans">NEW</span>}
                                      </td>
                                      <td className="px-2 py-1.5 text-slate-600 max-w-[200px] truncate">{dtc.description || '-'}</td>
                                      <td className="px-2 py-1.5">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                          dtc.status === 'Active' || dtc.status === 'Confirmed' 
                                            ? 'bg-red-100 text-red-700 border-red-200'
                                            : dtc.status === 'Pending' 
                                              ? 'bg-amber-100 text-amber-700 border-amber-200'
                                              : 'bg-slate-100 text-slate-600 border-slate-200'
                                        }`}>
                                          {dtc.status || '-'}
                                        </span>
                                      </td>
                                      <td className="px-2 py-1.5 text-slate-500">{dtc.ecuSource || dtc.category || '-'}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 py-2 text-sm text-emerald-600">
                            <CheckCircle className="h-4 w-4" />
                            <span>Clean scan - no DTC codes found</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Refresh button */}
          {!loading && scans.length > 0 && (
            <div className="pt-1 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchHistory}
                className="text-xs text-slate-500 hover:text-slate-700"
                data-testid="scan-history-refresh"
              >
                <History className="h-3 w-3 mr-1" />
                Refresh History
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScanHistory;
